/**
 * React Query hooks for the legacy_trust analytics endpoints (Phase 6b).
 *
 * All endpoints are CBO-scoped server-side; the caller never supplies cbo_id.
 * Used by components/communications/insights/* cards.
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { authFetch } from '@/lib/authFetch';
import type { AnalyticsScope, ReplyTrendResponse } from './types';

const STALE_MS = 10 * 60 * 1000; // 10 min — materialized views refresh nightly

async function fetchJson<T>(path: string): Promise<T> {
  const resp = await authFetch(path);
  if (!resp.ok) {
    throw new Error(`analytics fetch failed: ${path} ${resp.status}`);
  }
  return resp.json();
}

/** Build a drill-down or base URL. drillCboId only honored by internal CBO. */
function _basePath(suffix: string, drillCboId: number | null | undefined): string {
  if (drillCboId && drillCboId > 0) {
    return `/api/legacy-trust-analytics/cbo/${drillCboId}/${suffix}`;
  }
  return `/api/legacy-trust-analytics/${suffix}`;
}

export function useReplyTrend(days: number = 0, drillCboId?: number | null) {
  // days=0 → all history since 2021 (default). Backend interprets 0 as "no rolling window".
  return useQuery<ReplyTrendResponse>({
    queryKey: ['legacy-trust-analytics', 'reply-trend', days, drillCboId ?? null],
    queryFn: () => fetchJson<ReplyTrendResponse>(`${_basePath('reply-trend', drillCboId)}?days=${days}`),
    staleTime: STALE_MS,
  });
}

export function useCampaignPerformance(limit: number = 200, drillCboId?: number | null) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; campaigns: Record<string, unknown>[] }>({
    queryKey: ['legacy-trust-analytics', 'campaign-performance', limit, drillCboId ?? null],
    queryFn: () => fetchJson(`${_basePath('campaign-performance', drillCboId)}?limit=${limit}`),
    staleTime: STALE_MS,
  });
}

export interface ECMPriorityMember {
  legacy_cbo_id: number;
  legacy_trust_member_id: number;
  enroll_probability: number;
  priority_band: 'high' | 'medium' | 'low';
  current_status: string | null;
  is_actionable: boolean;
  model_version: string;
  scored_at: string;
}

export interface ECMPrioritySummaryRow {
  legacy_cbo_id: number;
  total_scored: number;
  actionable: number;
  actionable_high: number;
  actionable_medium: number;
  model_version: string;
  scored_at: string;
}

export interface ECMEnrollmentPriorityResponse {
  cbo_id: number;
  scope: AnalyticsScope;
  summary: { by_cbo: ECMPrioritySummaryRow[] };
  members: ECMPriorityMember[];
  note?: string;
}

/** Internal-CBO only — ECM enrollment-propensity ranking for outreach
 * prioritisation. Backend returns an empty list with a note for non-internal. */
export function useECMEnrollmentPriority(limit: number = 100, actionableOnly: boolean = true) {
  return useQuery<ECMEnrollmentPriorityResponse>({
    queryKey: ['legacy-trust-analytics', 'ecm-enrollment-priority', limit, actionableOnly],
    queryFn: () =>
      fetchJson(
        `/api/legacy-trust-analytics/ecm-enrollment-priority?limit=${limit}&actionable_only=${actionableOnly}`,
      ),
    staleTime: STALE_MS,
    retry: false,
  });
}

export interface DemographicCell {
  race: string;
  ethnicity: string;
  member_language: string;
  age_cohort: string;
  engagements: number;
  replies: number;
  reply_rate_pct: number | null;
  unique_members: number;
}

export function useDemographicEquity(drillCboId?: number | null) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; cells: DemographicCell[]; suppression_threshold: number }>({
    queryKey: ['legacy-trust-analytics', 'demographics', drillCboId ?? null],
    queryFn: () => fetchJson(_basePath('demographics', drillCboId)),
    staleTime: STALE_MS,
  });
}

export function useLifecycle(drillCboId?: number | null) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; monthly_cohorts: Record<string, unknown>[] }>({
    queryKey: ['legacy-trust-analytics', 'lifecycle', drillCboId ?? null],
    queryFn: () => fetchJson(_basePath('lifecycle', drillCboId)),
    staleTime: STALE_MS,
  });
}

export function useCBOSummary(drillCboId?: number | null) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; summary: Record<string, unknown> | null }>({
    queryKey: ['legacy-trust-analytics', 'summary', drillCboId ?? null],
    queryFn: () => fetchJson(_basePath('summary', drillCboId)),
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// Programs (intent) — Phase 6c, Gap 3
// ---------------------------------------------------------------------------

export interface ProgramRow {
  legacy_program_id: number;
  program_name: string | null;
  sponsor: string | null;
  cbos_running: number;
  members_targeted: number;
  total_engagements: number;
  total_delivered: number;
  total_replies: number;
  total_clicks: number;
  reply_rate_pct: number | null;
  click_rate_pct: number | null;
  engagement_rate_pct: number | null;
}

export function usePrograms(drillCboId?: number | null) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; programs: ProgramRow[] }>({
    queryKey: ['legacy-trust-analytics', 'programs', drillCboId ?? null],
    queryFn: () => fetchJson(_basePath('programs', drillCboId)),
    staleTime: STALE_MS,
  });
}

export interface ProgramBreakdownCell {
  race: string;
  ethnicity: string;
  member_language: string;
  age_cohort: string;
  engagements: number;
  delivered: number;
  replies: number;
  clicks: number;
  any_engagement: number;
  unique_members: number;
  reply_rate_pct: number | null;
  click_rate_pct: number | null;
  engagement_rate_pct: number | null;
}

export function useProgramBreakdown(
  programId: number | null | undefined,
  drillCboId?: number | null,
) {
  return useQuery<{
    cbo_id: number;
    scope: AnalyticsScope;
    program_id: number;
    cells: ProgramBreakdownCell[];
    suppression_threshold: number;
  }>({
    queryKey: ['legacy-trust-analytics', 'program-breakdown', programId, drillCboId ?? null],
    queryFn: () => {
      const base = drillCboId
        ? `/api/legacy-trust-analytics/cbo/${drillCboId}/program/${programId}/breakdown`
        : `/api/legacy-trust-analytics/program/${programId}/breakdown`;
      return fetchJson(base);
    },
    enabled: Boolean(programId && programId > 0),
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// Outreach intelligence — Phase 6d
// ---------------------------------------------------------------------------

export interface VelocityPoint {
  send_week: string;
  message_language: string;
  sends: number;
  engagements: number;
  unique_recipients: number;
}

export function useOutreachVelocity(weeks: number = 26) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; weeks: number; points: VelocityPoint[] }>({
    queryKey: ['legacy-trust-analytics', 'outreach-velocity', weeks],
    queryFn: () =>
      fetchJson(`/api/legacy-trust-analytics/outreach-velocity?weeks=${weeks}`),
    staleTime: STALE_MS,
  });
}

export interface LanguageMagnitudeRow {
  message_language: string;
  sends_12m: number;
  sends_90d: number;
  sends_30d: number;
  engagements_12m: number;
  engagements_90d: number;
  unique_recipients_12m: number;
  engagement_rate_12m_pct: number | null;
  engagement_rate_90d_pct: number | null;
  avg_daily_sends_90d: number | null;
}

export function useLanguageMagnitude() {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; languages: LanguageMagnitudeRow[] }>({
    queryKey: ['legacy-trust-analytics', 'language-magnitude'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/language-magnitude'),
    staleTime: STALE_MS,
  });
}

export interface IntentRow {
  inferred_intent: string;
  message_language: string;
  engagement_count: number;
  total_sms_sent: number;
  total_sms_delivered: number;
  first_seen: string | null;
  last_seen: string | null;
}

export function useIntentBreakdown() {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; rows: IntentRow[] }>({
    queryKey: ['legacy-trust-analytics', 'intent-breakdown'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/intent-breakdown'),
    staleTime: STALE_MS,
  });
}

export interface GeoCell {
  state: string;
  county: string;
  zip3: string;
  sends: number;
  engagements: number;
  unique_members: number;
  engagement_rate_pct: number | null;
}

export function useGeoDistribution() {
  return useQuery<{
    cbo_id: number;
    scope: AnalyticsScope;
    cells: GeoCell[];
    zip_granularity: string;
    suppression_threshold: number;
  }>({
    queryKey: ['legacy-trust-analytics', 'geo-distribution'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/geo-distribution'),
    staleTime: STALE_MS,
  });
}

export interface MemberStateRow {
  state: string;
  members: number;
  counties: number;
  zips: number;
  reachable_members: number;
}

export function useMemberGeoDistribution() {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; states: MemberStateRow[] }>({
    queryKey: ['legacy-trust-analytics', 'member-geo-distribution'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/member-geo-distribution'),
    staleTime: STALE_MS,
  });
}

export interface MemberCountyRow {
  state: string;
  county: string;
  members: number;
  zip_codes: number;
  reachable_members: number;
}

/** Per-county member counts. Pass a 2-letter state code to scope to one state. */
export function useMemberCountyDistribution(state?: string | null) {
  return useQuery<{ cbo_id: number; scope: AnalyticsScope; state_filter: string | null; counties: MemberCountyRow[] }>({
    queryKey: ['legacy-trust-analytics', 'member-county-distribution', state ?? null],
    queryFn: () => {
      const qs = state ? `?state=${encodeURIComponent(state)}` : '';
      return fetchJson(`/api/legacy-trust-analytics/member-county-distribution${qs}`);
    },
    staleTime: STALE_MS,
  });
}

/** All CBOs that have legacy data. Internal-only on the backend. */
export interface CBORow {
  cbo_id: number;
  cbo_name: string | null;
  cbo_type: string | null;
  members_count: number;
  engagements_90d: number;
  recipients_90d: number;
  replies_90d: number;
  reply_rate_90d_pct: number | null;
  cost_per_recipient_90d: number | null;
}

export function useCBOList() {
  return useQuery<{ cbo_id: number; scope: string; cbos: CBORow[] }>({
    queryKey: ['legacy-trust-analytics', 'cbos'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/cbos'),
    staleTime: STALE_MS,
    // 403 from non-internal CBOs — don't retry forever.
    retry: false,
  });
}

export interface MemberEngagementSummary {
  legacy_member_id: number;
  legacy_cbo_id: number;
  first_seen_ts: string | null;
  member_language: string | null;
  state: string | null;
  county: string | null;
  city: string | null;
  zip3: string | null;          // 3-digit ZIP per HIPAA Safe Harbor
  gender: string | null;
  race: string | null;
  ethnicity: string | null;
  dob_year: number | null;
  has_email: boolean;
  has_phone: boolean;
  completeness_score: number;
  reachable: string | null;
  lifetime_sends: number;
  lifetime_replies: number;
  lifetime_clicks: number;
  last_engagement_ts: string | null;
}

export function useMemberEngagementSummary(
  memberId: number | null | undefined,
  drillCboId?: number | null,
) {
  return useQuery<{ cbo_id: number; member_id: number; summary: MemberEngagementSummary | null }>({
    queryKey: ['legacy-trust-analytics', 'member-summary', memberId, drillCboId ?? null],
    queryFn: () => {
      const path = drillCboId
        ? `/api/legacy-trust-analytics/cbo/${drillCboId}/member/${memberId}/summary`
        : `/api/legacy-trust-analytics/member/${memberId}/summary`;
      return fetchJson(path);
    },
    enabled: Boolean(memberId && memberId > 0),
    staleTime: STALE_MS,
  });
}

export interface MemberTimelinePoint {
  month: string;
  sends: number;
  replies: number;
  clicks: number;
}

// ---------------------------------------------------------------------------
// Phase 7 — Federation, Vulnerability, Outcome Calibration, Benchmarks, Q&A
// All internal-CBO-only (backend returns 403 for non-internal).
// ---------------------------------------------------------------------------

export interface FederationRow {
  federation_code: string;
  program_code: string | null;
  age_cohort_max_dob_year: number | null;
  fpl_correlated: boolean;
  cbo_count: number;
  sends: number;
  delivered: number;
  replies: number;
  clicks: number;
  unique_members_engaged: number;
  first_send_ts: string | null;
  last_send_ts: string | null;
  members_total: number;
  members_in_age_cohort: number;
  members_non_english: number;
  members_no_email: number;
  members_with_phone: number;
  members_no_postal: number | null;
  members_phone_only: number | null;
  members_unreachable: number | null;
  reply_rate_pct: number | null;
  click_rate_pct: number | null;
  engagement_rate_pct: number | null;
  reach_efficiency_pct: number | null;
  time_to_first_reply_p50_h: number | null;
  geo_concentration_top3_pct: number | null;
  geo_top3_counties: string | null;
}

export function useFederationRollup() {
  return useQuery<{ cbo_id: number; scope: string; federations: FederationRow[] }>({
    queryKey: ['legacy-trust-analytics', 'federation'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/federation'),
    staleTime: STALE_MS,
    retry: false,
  });
}

// Environmental (FEMA disaster) exposure + engagement gap per federation.
// engagement_gap_pct > 0 means disaster-county members engage LESS (actionable).
export interface EnvFederationImpactRow {
  federation_code: string;
  members: number;
  in_disaster_county: number;
  pct_disaster_county: number | null;
  in_fire_county: number;
  engaged_pct_disaster: number | null;
  engaged_pct_other: number | null;
  engagement_gap_pct: number | null;
}

export function useEnvFederationImpact() {
  return useQuery<{ cbo_id: number; scope: string; federations: EnvFederationImpactRow[] }>({
    queryKey: ['legacy-trust-analytics', 'federation', 'environmental-impact'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/federation/environmental-impact'),
    staleTime: STALE_MS,
    retry: false,
  });
}

export interface FederationCBORow {
  federation_code: string;
  cbo_id: number;
  cbo_name: string;
  cbo_short_name: string | null;
  sends: number;
  replies: number;
  clicks: number;
  unique_members: number;
  first_send_ts: string | null;
  last_send_ts: string | null;
}

export function useFederationCBODetail(federationCode: string | null) {
  return useQuery<{ cbo_id: number; federation_code: string; cbos: FederationCBORow[] }>({
    queryKey: ['legacy-trust-analytics', 'federation-cbos', federationCode],
    queryFn: () => fetchJson(`/api/legacy-trust-analytics/federation/${federationCode}/cbos`),
    enabled: Boolean(federationCode),
    staleTime: STALE_MS,
    retry: false,
  });
}

export interface VulnerabilityRow {
  federation_code: string;
  vulnerability_band: 'HIGH' | 'MEDIUM' | 'LOW';
  members: number;
  avg_score: number | null;
  members_non_english: number;
  members_no_email: number;
  members_unreachable: number;
  members_older_adult: number;
  members_incomplete: number;
}

export function useVulnerabilityByFederation() {
  return useQuery<{
    cbo_id: number;
    rows: VulnerabilityRow[];
    bands: string[];
    weights: Record<string, number>;
  }>({
    queryKey: ['legacy-trust-analytics', 'vulnerability'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/vulnerability/by-federation'),
    staleTime: STALE_MS,
    retry: false,
  });
}

export interface OutcomeCalibrationBandRow {
  federation_code: string;
  vulnerability_band: 'HIGH' | 'MEDIUM' | 'LOW';
  replies: number;
  clicks: number;
  unique_repliers: number;
  unique_clickers: number;
  enrollment_ratio: number | null;
  nav_support_ratio: number | null;
  projected_enrollments: number;
  projected_nav_supports: number;
}

export interface OutcomeCalibrationTotalRow {
  federation_code: string;
  replies: number;
  clicks: number;
  unique_repliers: number;
  unique_clickers: number;
  projected_enrollments: number;
  projected_nav_supports: number;
}

export interface CalibrationFactorRow {
  federation_code: string;
  program_code: string;
  outcome_label: string;
  driver_metric: string;
  ratio: number;
  effective_from: string;
  source: string;
  notes: string | null;
}

export function useOutcomeCalibration(federationCode?: string | null) {
  return useQuery<{
    cbo_id: number;
    federation_filter: string | null;
    by_band: OutcomeCalibrationBandRow[];
    totals: OutcomeCalibrationTotalRow[];
    active_factors: CalibrationFactorRow[];
  }>({
    queryKey: ['legacy-trust-analytics', 'outcome-calibration', federationCode ?? null],
    queryFn: () => {
      const qs = federationCode ? `?federation_code=${encodeURIComponent(federationCode)}` : '';
      return fetchJson(`/api/legacy-trust-analytics/outcome-calibration${qs}`);
    },
    staleTime: STALE_MS,
    retry: false,
  });
}

export interface BenchmarkRow {
  metric_key: string;
  metric_label: string;
  cohort: string;
  value_low: number | null;
  value_high: number | null;
  value_point: number | null;
  unit: string;
  source_name: string;
  source_url: string | null;
  effective_year: number;
  category: string;
  comparison_note: string | null;
}

export function useExternalBenchmarks(category?: string | null) {
  return useQuery<{ cbo_id: number; category_filter: string | null; benchmarks: BenchmarkRow[] }>({
    queryKey: ['legacy-trust-analytics', 'external-benchmarks', category ?? null],
    queryFn: () => {
      const qs = category ? `?category=${encodeURIComponent(category)}` : '';
      return fetchJson(`/api/legacy-trust-analytics/external-benchmarks${qs}`);
    },
    staleTime: STALE_MS,
  });
}

export interface RetrievedChunk {
  source_type: string;
  federation_code: string | null;
  intent_label: string | null;
  language: string | null;
  similarity: number;
  preview: string;
}

export interface QAResponse {
  cbo_id: number;
  question: string;
  answer: string;
  citations: string[];
  model: string;
  evidence_keys?: string[];
  retrieved_chunks?: RetrievedChunk[];
  error?: string;
}

export function useMemberEngagementTimeline(
  memberId: number | null | undefined,
  months: number = 12,
  drillCboId?: number | null,
) {
  return useQuery<{
    cbo_id: number;
    member_id: number;
    months: number;
    points: MemberTimelinePoint[];
  }>({
    queryKey: ['legacy-trust-analytics', 'member-timeline', memberId, months, drillCboId ?? null],
    queryFn: () => {
      const path = drillCboId
        ? `/api/legacy-trust-analytics/cbo/${drillCboId}/member/${memberId}/timeline?months=${months}`
        : `/api/legacy-trust-analytics/member/${memberId}/timeline?months=${months}`;
      return fetchJson(path);
    },
    enabled: Boolean(memberId && memberId > 0),
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// Phase 7g — Reply intent classifier
// ---------------------------------------------------------------------------

export interface ReplyIntentRow {
  federation_code: string;
  program_code: string;
  intent_label: string;
  sentiment: string;
  urgency: string;
  language: string;
  reply_count: number;
  unique_members: number;
  opt_outs: number;
  first_seen: string | null;
  last_seen: string | null;
}

export interface ReplyQualityRow {
  federation_code: string;
  replies_total: number;
  replies_quality: number;
  replies_opt_out: number;
  replies_unclassified: number;
  quality_pct: number | null;
  opt_out_pct: number | null;
}

export function useReplyIntent(federationCode?: string | null) {
  return useQuery<{
    cbo_id: number;
    federation_filter: string | null;
    rollup: ReplyIntentRow[];
    quality: ReplyQualityRow[];
  }>({
    queryKey: ['legacy-trust-analytics', 'reply-intent', federationCode ?? null],
    queryFn: () => {
      const qs = federationCode ? `?federation_code=${encodeURIComponent(federationCode)}` : '';
      return fetchJson(`/api/legacy-trust-analytics/reply-intent${qs}`);
    },
    staleTime: STALE_MS,
    retry: false,
  });
}

export interface ReplyIntentSampleRow {
  body_norm_hash: string;
  sample_body: string;
  confidence: number | null;
  sentiment: string;
  urgency: string;
  detected_language: string;
  is_opt_out: boolean;
  classified_at: string;
}

export function useReplyIntentSamples(intentLabel: string | null, limit: number = 10) {
  return useQuery<{ cbo_id: number; intent_label: string; samples: ReplyIntentSampleRow[] }>({
    queryKey: ['legacy-trust-analytics', 'reply-intent-samples', intentLabel, limit],
    queryFn: () =>
      fetchJson(`/api/legacy-trust-analytics/reply-intent/${intentLabel}/samples?limit=${limit}`),
    enabled: Boolean(intentLabel),
    staleTime: STALE_MS,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Phase 8b — Monthly outcome time-series
// ---------------------------------------------------------------------------

export interface OutcomeMonthlyTotalRow {
  federation_code: string;
  month: string;          // 'YYYY-MM-DD' (1st of month)
  sends: number;
  replies: number;
  clicks: number;
  unique_repliers: number;
  unique_clickers: number;
  projected_enrollments: number;
  projected_nav_supports: number;
}

export interface OutcomeMonthlyByBandRow extends OutcomeMonthlyTotalRow {
  vulnerability_band: 'HIGH' | 'MEDIUM' | 'LOW';
}

export function useOutcomeMonthly(federationCode?: string | null, months = 36) {
  return useQuery<{
    cbo_id: number;
    federation_filter: string | null;
    months_requested: number;
    totals: OutcomeMonthlyTotalRow[];
    by_band: OutcomeMonthlyByBandRow[];
  }>({
    queryKey: ['legacy-trust-analytics', 'outcome-monthly', federationCode ?? null, months],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (federationCode) qs.set('federation_code', federationCode);
      qs.set('months', String(months));
      return fetchJson(`/api/legacy-trust-analytics/outcome-monthly?${qs.toString()}`);
    },
    staleTime: STALE_MS,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Phase 8e — Cross-cohort comparison
// ---------------------------------------------------------------------------

export interface CrossCohortRow {
  federation_code: string;
  program_code: string | null;
  cbo_count: number;
  sends: number;
  replies: number;
  clicks: number;
  unique_members_engaged: number;
  members_total: number;
  reply_rate_pct: number | null;
  click_rate_pct: number | null;
  engagement_rate_pct: number | null;
  pct_non_english: number | null;
  pct_no_email: number | null;
  pct_age_cohort: number | null;
  population_vulnerability_index: number | null;
  members_high_band: number | null;
  members_medium_band: number | null;
  enrollment_outcome_label: string | null;
  enrollment_ratio: number | null;
  nav_support_outcome_label: string | null;
  nav_support_ratio: number | null;
  projected_enrollments: number | null;
  projected_nav_supports: number | null;
  replies_total: number | null;
  reply_quality_pct: number | null;
  reply_opt_out_pct: number | null;
  sentiment_positive_pct: number | null;
  sentiment_negative_pct: number | null;
  net_sentiment_score: number | null;
}

// ---------------------------------------------------------------------------
// Phase 8j — Sentiment (derived from existing reply_intent_llm.sentiment)
// ---------------------------------------------------------------------------

export interface SentimentByFederationRow {
  federation_code: string;
  replies_classified: number;
  replies_positive: number;
  replies_neutral: number;
  replies_negative: number;
  positive_pct: number | null;
  negative_pct: number | null;
  net_sentiment_score: number | null;
}

export interface SentimentMonthlyRow {
  federation_code: string;
  month: string;
  replies_positive: number;
  replies_neutral: number;
  replies_negative: number;
  replies_total: number;
}

export function useSentiment(federationCode?: string | null, months = 24) {
  return useQuery<{
    cbo_id: number;
    federation_filter: string | null;
    by_federation: SentimentByFederationRow[];
    monthly: SentimentMonthlyRow[];
  }>({
    queryKey: ['legacy-trust-analytics', 'sentiment', federationCode ?? null, months],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (federationCode) qs.set('federation_code', federationCode);
      qs.set('months', String(months));
      return fetchJson(`/api/legacy-trust-analytics/sentiment?${qs.toString()}`);
    },
    staleTime: STALE_MS,
    retry: false,
  });
}

export function useCrossCohort() {
  return useQuery<{ cbo_id: number; cohorts: CrossCohortRow[] }>({
    queryKey: ['legacy-trust-analytics', 'cross-cohort'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/cross-cohort'),
    staleTime: STALE_MS,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Phase 8d — Per-member vulnerability + latest reply intent
// ---------------------------------------------------------------------------

export interface MemberVulnerabilityDetail {
  legacy_member_id: number;
  legacy_cbo_id: number;
  federation_code: string | null;
  program_code: string | null;
  vulnerability_score: number;
  vulnerability_band: 'HIGH' | 'MEDIUM' | 'LOW';
  f_non_english: number;
  f_no_email: number;
  f_unreachable: number;
  f_older_adult: number;
  f_incomplete: number;
  f_federation: number;
  language: string | null;
  race: string | null;
  ethnicity: string | null;
  gender: string | null;
  dob_year: number | null;
  state: string | null;
  county: string | null;
  latest_reply_ts: string | null;
  latest_intent_label: string | null;
  sentiment: string | null;
  urgency: string | null;
  is_opt_out: boolean | null;
}

export function useMemberVulnerability(memberId: number | null | undefined, drillCboId?: number | null) {
  return useQuery<{ cbo_id: number; member_id: number; detail: MemberVulnerabilityDetail | null }>({
    queryKey: ['legacy-trust-analytics', 'member-vulnerability', memberId, drillCboId ?? null],
    queryFn: () => {
      const path = drillCboId
        ? `/api/legacy-trust-analytics/cbo/${drillCboId}/member/${memberId}/vulnerability`
        : `/api/legacy-trust-analytics/member/${memberId}/vulnerability`;
      return fetchJson(path);
    },
    enabled: Boolean(memberId && memberId > 0),
    staleTime: STALE_MS,
  });
}

// ---------------------------------------------------------------------------
// Phase 10f — SDOH + ML retention overlay per federation
// ---------------------------------------------------------------------------

export interface SdohOverlayRow {
  federation_code: string;
  // SDOH (matched via identity_link)
  sdoh_matched_members: number | null;
  avg_walk_score: number | null;
  avg_transit_score: number | null;
  avg_bike_score: number | null;
  avg_climate_risk_score: number | null;
  avg_air_quality_index: number | null;
  avg_eviction_rate: number | null;
  avg_housing_cost_burden: number | null;
  avg_housing_stability_score: number | null;
  avg_link_confidence: number | null;
  // ML retention (matched members)
  retention_matched_members: number | null;
  retention_healthy: number | null;
  retention_watch: number | null;
  retention_at_risk: number | null;
  retention_critical: number | null;
  retention_lost: number | null;
  retention_improving: number | null;
  retention_declining: number | null;
  retention_rapid_decline: number | null;
  retention_avg_engagement: number | null;
  retention_avg_recency: number | null;
  // Proxy for unmatched
  proxy_members: number | null;
  proxy_healthy: number | null;
  proxy_watch: number | null;
  proxy_at_risk: number | null;
  proxy_critical: number | null;
  proxy_avg_score: number | null;
  // Phase 10g — Retention Burden Index + burden-adjusted engagement
  retention_burden_index: number | null;
  burden_adjusted_engagement_rate_pct: number | null;
}

export function useSdohOverlay() {
  return useQuery<{ cbo_id: number; rows: SdohOverlayRow[] }>({
    queryKey: ['legacy-trust-analytics', 'sdoh-overlay'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/sdoh-overlay'),
    staleTime: STALE_MS,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Phase 10c — Engagement quality (out_22b) per-federation rollup
// ---------------------------------------------------------------------------

export interface EngagementQualityRow {
  federation_code: string;
  band: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  members: number;
  avg_score: number | null;
  avg_clicks: number | null;
  avg_downloads: number | null;
  avg_face_time_signals: number | null;
  members_legacy_matched: number;
}

export function useEngagementQuality() {
  return useQuery<{ cbo_id: number; rows: EngagementQualityRow[] }>({
    queryKey: ['legacy-trust-analytics', 'engagement-quality'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/engagement-quality'),
    staleTime: STALE_MS,
    retry: false,
  });
}

// ---------------------------------------------------------------------------
// Phase 8f — Curated demo stories
// ---------------------------------------------------------------------------

export interface DemoStory {
  key: string;
  title: string;
  question: string;
  tags: string[];
}

export function useDemoStories() {
  return useQuery<{ cbo_id: number; stories: DemoStory[] }>({
    queryKey: ['legacy-trust-analytics', 'demo-stories'],
    queryFn: () => fetchJson('/api/legacy-trust-analytics/demo-stories'),
    staleTime: 60 * 60 * 1000,
    retry: false,
  });
}

export function useLegacyTrustQA() {
  return useMutation<QAResponse, Error, { question: string }>({
    mutationFn: async ({ question }) => {
      const resp = await authFetch('/api/legacy-trust-analytics/qa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      if (!resp.ok) {
        throw new Error(`QA request failed: ${resp.status}`);
      }
      return resp.json();
    },
  });
}
